import { useEffect, useRef } from "react";
import { InvalidateQueryFilters, useQueryClient } from "@tanstack/react-query";
import { getEcho } from "../bridges/echo";
import { useCurrentUser, useWorkspaceId } from "../bridges/session";
import { scopedKey } from "./keys";

type Options = {
  /** The keys to refresh, read from the event's payload (e.g. the account it names); replaces `keys` when given. */
  keysFor?: (payload: unknown) => unknown[][];
  /** More queries to refresh, as full filters (e.g. only those on screen), read from the payload. */
  filtersFor?: (payload: unknown) => InvalidateQueryFilters[];
};

/** When `event` arrives on the private `channel`, refresh the given queries (or those `keysFor` reads from it). */
export const useRealtimeInvalidation = (channel: string | null, event: string, keys: unknown[][], opts: Options = {}) => {
  const qc = useQueryClient();
  const keyStr = JSON.stringify(keys);
  // Read through a ref: a fresh function every render must not re-subscribe.
  const keysFor = useRef(opts.keysFor);
  keysFor.current = opts.keysFor;
  const filtersFor = useRef(opts.filtersFor);
  filtersFor.current = opts.filtersFor;
  useEffect(() => {
    const echo = getEcho();
    if (!channel || !echo) return;
    const ch = echo.private(channel);
    const handler = (payload: unknown) => {
      const list = keysFor.current ? keysFor.current(payload) : (JSON.parse(keyStr) as unknown[][]);
      for (const k of list) qc.invalidateQueries({ queryKey: k });
      for (const f of filtersFor.current?.(payload) ?? []) qc.invalidateQueries(f);
    };
    ch.listen(event, handler);
    return () => { ch.stopListening(event, handler); };
  }, [channel, event, keyStr, qc]);
};

/** The event the server sends a user when his access (or his account's access lists) changed: ids only, no names. */
export const ACCESS_CHANGED_EVENT = "NextAccessChanged";

/**
 * Access changed (role, switches, reach, a hand-over…): re-read what this person may do and the account basics.
 * The event names the account it is about; a background account's cache is refreshed too. Mount it ONCE, in the shell
 * (F3), never per page.
 */
export const useAccessLive = () => {
  const ws = useWorkspaceId();
  const userId = useCurrentUser()?.id;
  useRealtimeInvalidation(userId ? `admin.${userId}` : null, ACCESS_CHANGED_EVENT, [scopedKey(ws, "access"), scopedKey(ws, "account-basics")], {
    keysFor: (payload) => {
      const named = (payload as { workspace_id?: unknown } | null)?.workspace_id;
      const target = typeof named === "number" ? named : ws;
      return [scopedKey(target, "access"), scopedKey(target, "account-basics")];
    },
  });
};
